// Named sort orderings for the pharmacy Orders picklist, plus the dropdown's own
// persistence.
//
// Each preset is now a set of `orderBy`/`order` query params sent straight through to
// GET /userOrder/admin (index.jsx), NOT a client-side comparator. That used to be a
// client-side re-sort of whatever 20-row page the backend happened to return
// (paginated by id DESC) -- which meant the sort only ever reordered the current page,
// and could never surface the true most-urgent order across the whole filtered set if
// it landed on a different page. Sorting server-side, before LIMIT/OFFSET is applied,
// fixes that: page 1 of "Most Urgent" is now genuinely the 20 most urgent orders out of
// everything matching the current tab/search, not just whichever 20 the id-ordered
// pagination happened to hand back.
//
// `pickup_priority` maps to a sentinel `orderBy` value, `pickupPriority` --
// classes/UserOrder/Functions.js (pillup-api) special-cases it into a composite
// ORDER BY (startDate, then a Delhi-NCR CASE expression, then createdAt), since it
// isn't a single column the generic getOrderBy() mechanism can express. The other
// three map directly onto real DATE columns (`endDate`, `createdAt`) that endpoint
// already supports generically -- no backend change was needed for those.
export const SORT_PRESETS = {
    pickup_priority: {
        label: "Pickup priority",
        caption:
            "Sorted by pickup priority: earliest start date first, then Delhi NCR, then whoever ordered first, across every order matching this tab -- not just this page.",
        orderBy: "pickupPriority",
    },
    most_urgent: {
        label: "Most Urgent",
        caption:
            "Sorted by urgency: soonest running out of medicine first (Days Left ascending), across every order matching this tab.",
        orderBy: "endDate",
        order: "ASC",
    },
    newest_first: {
        label: "Newest orders first",
        caption: "Sorted by order date: most recently placed first, across every order matching this tab.",
        orderBy: "createdAt",
        order: "DESC",
    },
    oldest_first: {
        label: "Oldest orders first",
        caption: "Sorted by order date: oldest unprocessed order first, across every order matching this tab.",
        orderBy: "createdAt",
        order: "ASC",
    },
};

export const DEFAULT_SORT_PRESET = "pickup_priority";

const STORAGE_KEY = "pillup.pharmacy.orders.sortPreset";

/**
 * The user's stored sort preset, or the default.
 *
 * Never throws -- localStorage can be unavailable or the stored value malformed/stale
 * (a preset id that no longer exists in SORT_PRESETS), and a sort preference is not
 * worth failing a page render over.
 */
export function loadSortPreset() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw || !(raw in SORT_PRESETS)) return DEFAULT_SORT_PRESET;
        return raw;
    } catch (error) {
        return DEFAULT_SORT_PRESET;
    }
}

/** Persist the chosen preset id. Silently no-ops if storage is unavailable. */
export function saveSortPreset(id) {
    try {
        window.localStorage.setItem(STORAGE_KEY, id);
    } catch (error) {
        // Ignored on purpose -- the choice still applies for this session.
    }
}
